// Display only: never use this value for matching or API writes.
export function calculationAmount(result: { status?: string; summary: Record<string, unknown> } | null | undefined, field = "requested_amount"): string | null {
  if (!result || result.status === "blocked" || result.status === "failed" || result.summary.independent_basis_missing) return null;
  const amount = result.summary[field];
  return amount === null || amount === undefined || amount === "" ? null : String(amount);
}

export function formatAccountCode(value: string | null | undefined): string {
  return (value || "").replace(/-/g, ".");
}

export function formatAccountCodeText(value: string | null | undefined): string {
  // Only account-labelled runs; dates, invoice/product numbers and prose stay intact.
  return (value || "").replace(
    /(\b(?:hesap(?:\s+kod(?:u|ları|lari))?(?:lar[ıi]?)?\s*:?\s*|beklenirken\s+))([1-9]\d{2}(?:[.-]\d+)*(?:(?:\s*[,/]\s*|\s+ve\s+)[1-9]\d{2}(?:[.-]\d+)*)*)(?![\w-]|\.\d)/gi,
    (_, label: string, codes: string) => label + codes.replace(/-/g, ".")
  );
}

export function moneyNumber(value: string | number): string {
  return Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatMoney(value: string | number | null | undefined, currency = "TRY", locale = "en-US"): string {
  if (value === null || value === undefined || value === "") return "Tutar yok";
  try {
    return Number(value).toLocaleString(locale, {
      style: "currency", currency: currency || "TRY", currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 2, maximumFractionDigits: 2
    });
  } catch {
    return `${moneyNumber(value)} ${currency}`.trim();
  }
}

// Parse the selected display format into an ungrouped, dot-decimal API value.
// Incomplete/ambiguous or excess-precision values must not be silently changed.
export function parseMoneyInput(value: string, locale: "en-US" | "tr-TR" = "en-US"): string | null {
  const trimmed = locale === "tr-TR" ? value.trim().replace(/[.,]/g, separator => separator === "." ? "," : ".") : value.trim();
  if (!/^(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d{1,2})?$/.test(trimmed)) return null;
  const number = Number(trimmed.replaceAll(",", ""));
  if (!Number.isFinite(number) || number < 0 || number >= 1e14) return null;
  const [whole, fraction = ""] = trimmed.replaceAll(",", "").split(".");
  return `${whole.replace(/^0+(?=\d)/, "")}.${fraction.padEnd(2, "0")}`;
}

export function formatMoneyInput(value: string, editing = false, locale: "en-US" | "tr-TR" = "en-US"): string {
  if (!value) return "";
  if (value.includes(",") && parseMoneyInput(value) === null) return value;
  const plain = value.replaceAll(",", "");
  if (!/^\d*(\.\d{0,2})?$/.test(plain)) return value;
  const [whole, fraction] = plain.split(".");
  const grouped = (whole || "0").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const formatted = grouped + (editing
    ? fraction === undefined ? "" : `.${fraction}`
    : `.${(fraction || "").padEnd(2, "0")}`);
  return locale === "tr-TR" ? formatted.replace(/[.,]/g, separator => separator === "." ? "," : ".") : formatted;
}

export function formatMoneyText(text: string | undefined): string {
  return formatAccountCodeText(text).replace(/(?<![\d.,])(-?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?)\s+TL\b/g, (_, amount: string) => `${moneyNumber(amount.replaceAll(",", ""))} TL`);
}

export function unitName(unit: string) {
  return ({ KGM: "kg", KG: "kg", GRM: "gram", C62: "adet", EA: "adet", H87: "adet", ADET: "adet", MTR: "metre", LTR: "litre", SET: "set" } as Record<string, string>)[unit] || unit;
}
